import { Grid, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    CLIENT_PROFILE_STAGE_KEYS,
    DEFAULT_GOAL_CATEGORY,
    EWS_DURATION_KEYS,
    FEATURE_NAMES,
    getEwsDurationKeys,
    STATUSVARIANTS
} from '../../common/constants';
import { useAppDispatch, useAppSelector, useAppStore, useStalePlanRefresh } from '../../common/hooks';
import {ContinueStepButton, StatusChip, Typography } from '../../components/atoms';
import { ConfirmationCentered, InfoTooltip, StepperPills } from '../../components/molecules';
import {
    saveClient,
    updateCurrentStep,
    syncAdviceClient,
    setEnableConfirmConsoleNavigation
} from '../../features/client';
import {
    AdviceGoalPayloadType,
    getGoalCategory,
    mpSaveProposal,
    removeLoader,
    saveGoal,
    showLoader,
    syncAdviceGoals
} from '../../features/client-goals';
import { logEventToBackEnd, updateGlobalLoaderState } from '../../features/global/globalSlice';
import { setHasUnsavedProfileChanges } from '../../features/global/profileEditSlice';
import { clearGoalEdit } from '../../features/global/goalEditSlice';
import {
    selectEwsStepperCtaStatus,
    selectAffectedEwsTerms,
    selectEwsLoading,
    setAffectedEwsTerms,
    seedAdviceContinuityForReallocation,
    updatePlanContinuity
} from '../../features/ews';
import { clearUnsavedPlanChanges } from '../../features/unsaved-plan-changes';
import { CreateGoalsStep } from './CreateGoalsStep';
import { ReviewPlanDetailsStep } from './ReviewPlanDetailsStep';
import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useRef, useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Banner } from '../../components/molecules/Banner/Banner';
import PDFViewer from '../../components/organisms/PDF/PDFViewer';
import GeneratedPlanPDF from '../../components/organisms/PDF/GeneratedPDFs/GeneratedPlanPDF';
import { shouldPromptForAccounts, shouldPromptForIncome } from '../../common/helper';
import dayjs from 'dayjs';
import { StickyRegion } from '../../containers/sticky-region';
import {
    callSelectiveEWS,
    callRunpipe,
    checkFFEnabled,
    downloadPdfThroughBackend,
    PdfDownloadToastState,
    callMpGoalCalculator,
    callPlanExecution,
    getMissingFundingTenures,
    applyAccountFieldStripping,
    getEligibleGoals,
    callUnifiedEWS
} from '../../utils';
import { EwsDuration } from '../../common/constants';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { AddInvestmentAccounts } from '../../containers/add-investment-accounts';
import {
    hasPastGoalDate,
    isGoalTenureLocked,
    stampGoalsTenureLocked
} from '../../containers/goals-inline-flow2/goalInlineHelpers';
import { GoalCategoryType, GoalRecommendationType, GoalTypes } from '../../common/types';
import { StepperPillsProps } from '../../components/molecules/StepperPills/StepperPills';
import { ReactComponent as AccountIcon } from '../../assets/icons/clientProfileRevamped.svg';
import { ReactComponent as GoalsIcon } from '../../assets/icons/goalsRevamped.svg';
import { ReactComponent as PlanDetailsIcon } from '../../assets/icons/planDetailsRevamped.svg';
import { ReactComponent as RiskToleranceIcon } from '../../assets/icons/riskToleranceRevamped.svg';
import { ReactComponent as FundingNavigatorIcon } from '../../assets/icons/fundingNavigator.svg';
import { ReactComponent as PlanExecutionIcon } from '../../assets/icons/execution.svg';
import RiskToleranceStep from '../ClientJourneyCommon/RiskToleranceStep';
import { CreateClientStep } from './CreateClientStep';
import { ScrollerOption } from '../../components/molecules/VerticalScroller/VerticalScroller';
import { getRiskToleranceOptions } from '../../common/constants';
import { CreatePdfReportsModal } from '../../modals/CreatePdfReports';
import PlanOverview from '../ClientJourneyCommon/PlanOverview';
import { Toast } from '../../components/molecules/Toast/Toast';
import { PdfDownloadToast } from '../../components/molecules/Toast/PdfDownloadToast';
import {
    MarketPortfolios,
    Portfolio,
    TenantSupportedWeightType,
    goalCategoryPortfolioMappingsType
} from '../../common/types';
import FundingNavigatorStep from './FundingNavigatorStep';
import PlanExecutionSection from './PlanExecutionSection';

interface ClientProfileProps {
    // eslint-disable-next-line no-unused-vars
    updateStep?: (step: number | ((prev: number) => number)) => void;
    disabled?: boolean;
}

const StepContent = ({
    render,
    ...stepProps
}: ClientProfileProps & {
    // eslint-disable-next-line no-unused-vars
    render: (props: ClientProfileProps) => JSX.Element;
}) => render(stepProps);

const StyledBanner = styled('div')(({ theme }) => ({
    paddingBlock: theme.spacing(1)
}));

const StyleDivWithStatus = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
}));

interface ProfileLeaveConfirmModalHandle {
    // eslint-disable-next-line no-unused-vars
    open: (onLeave: () => void | Promise<void>) => void;
}

const ProfileLeaveConfirmModal = forwardRef<ProfileLeaveConfirmModalHandle>((_props, ref) => {
    const { t } = useTranslation();
    const [onLeave, setOnLeave] = useState<null | (() => void | Promise<void>)>(null);
    useImperativeHandle(
        ref,
        () => ({
            open: (leave: () => void | Promise<void>) => setOnLeave(() => leave)
        }),
        []
    );
    if (!onLeave) return null;
    const isNewModal = checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428']);
    const stay = () => setOnLeave(null);
    const leaveWorkflow = async () => {
        const leave = onLeave;
        setOnLeave(null);
        await leave?.();
    };
    return (
        <ConfirmationCentered
            title={t(isNewModal ? 'LEAVE_WORKFLOW_TITLE_FTDS' : 'DRAWER_CLOSE_MODAL_TITLE')}
            content={t(isNewModal ? 'LEAVE_WORKFLOW_CONTENT_FTDS' : 'DRAWER_CLOSE_MODAL_HEADER_TEXT')}
            confirmButtonLabel={t(
                isNewModal ? 'DELETE_CONFIRMATION_MODAL_DELETE_TEXT' : 'DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY'
            )}
            cancelButtonLabel={t(
                isNewModal ? 'DELETE_CONFIRMATION_MODAL_CANCEL_TEXT' : 'DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE'
            )}
            onConfirmation={isNewModal ? leaveWorkflow : stay}
            onCancellation={isNewModal ? stay : leaveWorkflow}
            onCloseIconClick={stay}
            open
        />
    );
});
ProfileLeaveConfirmModal.displayName = 'ProfileLeaveConfirmModal';

// Index of the Goals step in the journeySteps array (profile 0, risk 1, goals 2, ...).
const GOALS_STEP_INDEX = 2;
//styled elements
const JourneyContainer = styled('main')(() => ({
    display: 'flex',
    flexDirection: 'row',
    padding: '10px 40px 10px 40px',
    flexGrow: 1
    // marginBottom: '80px'
    // maxWidth: theme.breakpoints.values.lg // let's not add a maxWidth until we have a solution for the LeftPanel position
}));

const ClientJourneyFlow2 = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    // Subscribe only to the single stepper/CTA rollup (aggregate loading/error/ready/no-eligible)
    // so a streamed per-term response tick never re-renders the whole journey. The full
    // request/response maps are read non-reactively at save time (see handleSavePlan).
    const ewsStepperCtaStatus = useAppSelector(selectEwsStepperCtaStatus);
    // affectedEwsTerms (dirty terms) + ewsLoading now live in the ews slice, not adviceGoals.global.
    const affectedEwsTerms = useAppSelector(selectAffectedEwsTerms);
    const ewsLoading = useAppSelector(selectEwsLoading);
    // Inline Goals revamp (AEX-3446): true while a goal row is being created/edited inline.
    // Stays false in the legacy flow (only GoalsInlineTable sets it, behind the FF).
    const hasUnsavedGoalChanges = useAppSelector((state) => state.goalEdit?.editingRowKey != null);
    const isPlanSavedOnce = Boolean(adviceGoals?.global?.isPlanSavedOnce);

    // Clear any in-progress inline goal edit state when leaving the journey, so a half-started
    // add/edit never leaks into a different client/session (goalEdit is not persisted).
    useEffect(() => {
        return () => {
            dispatch(clearGoalEdit());
        };
    }, [dispatch]);
    const global = useAppSelector((state) => state.global);
    const store = useAppStore();
    const [openPDFModal, setOpenPDFModal] = useState(false);
    const [openPDFViewer, setOpenPDFViewer] = useState(false);
    // Legacy drawer-based Profile flow (used when the inline FF is off).
    const [openClientDrawer, setOpenClientDrawer] = useState(false);
    const [isRelationship, setIsRelationship] = useState(false);
    const [profileEditingRowKey, setProfileEditingRowKey] = useState<string | number | null>(null);
    const [profileAddingCoClient, setProfileAddingCoClient] = useState(false);
    // string '0' is needed for Tabs to function correctly
    const [tabIndex, setTabIndex] = useState('0');
    const [confirmModal, setConfirmModal] = useState<{
        title: string;
        content: string;
        confirmLabel: string;
        cancelLabel: string;
        cancelButtonFirst?: boolean;
        onProceed: () => void | Promise<void>;
    } | null>(null);
    const leaveModalRef = useRef<ProfileLeaveConfirmModalHandle>(null);
    const [factSheetsMissingToast, setFactSheetsMissingToast] = useState({ open: false, generating: false });
    const [pdfGenerateToast, setPdfGenerateToast] = useState<PdfDownloadToastState>({ open: false, generating: false });
    const [savePlanToast, setSavePlanToast] = useState<{ open: boolean; type: 'confirm' | 'error' }>({
        open: false,
        type: 'confirm'
    });
    const [savePlanError, setSavePlanError] = useState(false);
    // AEX-3363: surface the "Plan Has Changed" banner + Refresh Plan when the backend rejects the
    // Save Plan because the plan was edited in another tab (mpsave_proposal reference-ID mismatch).
    const { isStalePlan, markStaleFromError, refreshPlan } = useStalePlanRefresh(() => {
        setSavePlanError(false);
        setSavePlanToast({ open: false, type: 'confirm' });
    });
    const [activeDrawer, setActiveDrawer] = useState(false);
    // const planExecutionResponse = useAppSelector((state) => state.adviceGoals?.global?.planExecutionResponse);
    const marketPortfolios: MarketPortfolios = useAppSelector((state) => state.global?.marketPortfolios) as any;
    const weightTypes: TenantSupportedWeightType =
        useAppSelector((state) => state?.global?.globalConfig?.tenant_supported_weight_type) || 'both';
    const goalCategoryPortfolioMappings: goalCategoryPortfolioMappingsType | undefined = useAppSelector(
        (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings
    );
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const supportEmail = useAppSelector(
        (state) => state.global?.globalConfig?.supportEmail || t('APPLICATION_SUPPORT_EMAIL')
    );
    const isMultiPortfolioTenant = useAppSelector((state) => Boolean(state?.global?.globalConfig?.isMultiPortfolioTenant));
    const tactical: Portfolio[] = useMemo(() => {
        // AEX-3108: single-portfolio tenants hold one flat tactical/strategic set (no per-term portfolios).
        if (!isMultiPortfolioTenant) return marketPortfolios?.tactical || [];
        return [
            ...(marketPortfolios?.shortTerm?.tactical || []),
            ...(marketPortfolios?.mediumTerm?.tactical || []),
            ...(marketPortfolios?.longTerm?.tactical || [])
        ];
    }, [marketPortfolios, isMultiPortfolioTenant]);
    const strategic: Portfolio[] = useMemo(() => {
        if (!isMultiPortfolioTenant) return marketPortfolios?.strategic || [];
        return [
            ...(marketPortfolios?.shortTerm?.strategic || []),
            ...(marketPortfolios?.mediumTerm?.strategic || []),
            ...(marketPortfolios?.longTerm?.strategic || [])
        ];
    }, [marketPortfolios, tenant, isMultiPortfolioTenant]);
    let riskProfileFromVerticalScroller = adviceClient?.riskProfile || 'Moderate';
    const [hasRiskScrollerBeenTouched, setHasRiskScrollerBeenTouched] = useState(false);
    useEffect(() => {
        setHasRiskScrollerBeenTouched(false);
    }, [adviceClient?.step]);
    // Tracks whether risk tolerance is persisted in the BACKEND. We can't read this off the live
    // `adviceClient` at click time, because it mutates immediately on UI changes (e.g. the risk
    // step sets `riskProfile` to the 'Moderate' default on landing). Instead we seed this once from
    // the client as the backend first returns it — the risk stage key is only written to `stages`
    // on a real save — and flip it to true whenever we confirm a backend save.
    const riskPersistedInBackendRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (riskPersistedInBackendRef.current === null && adviceClient?._id) {
            riskPersistedInBackendRef.current = !!adviceClient?.stages?.includes(CLIENT_PROFILE_STAGE_KEYS[2]);
        }
    }, [adviceClient?._id, adviceClient?.stages]);
    const [adviceGoalsResponse, setAdviceGoalsResponse] = useState<AdviceGoalPayloadType['response']>(
        adviceGoals?.response
    );
    const [goalsWithMissingDates, setGoalsWithMissingDates] = useState<AdviceGoalPayloadType['formFields']>([]);

    const hasMissingFundingTenures = useMemo(
        () =>
            getMissingFundingTenures(
                adviceClient,
                adviceGoals,
                global?.orgInfo?.incomeSourceType?.en,
                global?.orgInfo?.investmentAccountType?.en
            ).length > 0,
        [adviceClient, adviceGoals, global?.orgInfo?.incomeSourceType?.en, global?.orgInfo?.investmentAccountType?.en]
    );

    const hasNoEligibleFunds = ewsStepperCtaStatus.hasNoEligibleFunds;

    // AEX-3430: goals whose timeline dates have elapsed on a reopened plan. Gates plan progression
    // (stepper beyond Goals + step CTAs) until the advisor updates them to valid future dates. The
    // page banner and per-row error UI live in the inline Goals table (GOALS_VIEW_REVAMP flow).
    const hasPastGoalDates = useMemo(
        () =>
            checkFFEnabled(FEATURE_NAMES['GOALS_VIEW_REVAMP_FE_AEX-3428']) &&
            !isPlanSavedOnce &&
            (adviceGoals?.formFields ?? []).some((goal) => hasPastGoalDate(goal.data)),
        [adviceGoals?.formFields]
    );

    // AEX-3430: on opening a plan whose goal timeline dates have elapsed, send the advisor to the
    // Profile step once. Ref-guarded so it fires a single time per mount and never re-traps the user
    // on Profile while they navigate to the Goals step to resolve the dates.
    const pastDatesRedirectHandledRef = useRef(false);
    useEffect(() => {
        if (!pastDatesRedirectHandledRef.current && hasPastGoalDates && (adviceClient?.step ?? 0) > 0) {
            pastDatesRedirectHandledRef.current = true;
            dispatch(updateCurrentStep(0));
        }
    }, [hasPastGoalDates, adviceClient?.step, dispatch]);

    const isClientAvailable = !!adviceClient?.clientFirstName;
    const isAccountIncomeMappingEnabled = checkFFEnabled(FEATURE_NAMES['ACCOUNT_INCOME_MAPPING_FE_AEX-3270']);
    const profileIsAddingNew = isAccountIncomeMappingEnabled && (profileAddingCoClient || !isClientAvailable);
    const profileDraftActive = isAccountIncomeMappingEnabled && (profileEditingRowKey !== null || profileIsAddingNew);
    // Whether the plan already holds information beyond the profile being added/edited: an already
    // saved/run plan, or a draft that has accounts, goals or a risk profile. Used to decide whether
    // navigating away from an in-progress Client/Co-Client edit is blocked (brand-new plan) or
    // allowed after a Stay/Leave confirmation (plan with other details).
    const planHasDetailsBeyondProfile = useMemo(() => {
        const hasAccounts =
            (adviceClient?.accounts?.incomeSources?.length ?? 0) > 0 ||
            (adviceClient?.accounts?.investmentsSources?.length ?? 0) > 0;
        const hasGoals = (adviceGoals?.formFields?.length ?? 0) > 0;
        const hasRisk = !!adviceClient?.riskProfile || (adviceClient?.step ?? 0) > 1;
        const isSavedPlan = !!adviceGoals?.global?.isPlanRun;
        return isSavedPlan || hasAccounts || hasGoals || hasRisk;
    }, [adviceClient, adviceGoals]);
    // Hard-disable Continue/Accounts tab/stepper pills only for a brand-new, profile-only plan. When
    // the plan has other details we keep them enabled and gate navigation behind a Stay/Leave modal.
    const profileDraftBlocks = profileDraftActive && !planHasDetailsBeyondProfile;
    const riskProfiles = global?.goeConfig?.riskProfiles || [];
    const minMaxEquity = global?.goeConfig?.minMaxEquity || {};
    const RISK_TOLERANCE_OPTIONS = getRiskToleranceOptions(t, riskProfiles, minMaxEquity);

    // Plan execution (Rebalancer) is ready only when the async call succeeded and returned a
    // usable rebalancerResult. This single readiness rule gates both the Save Plan UI state and
    // the Save Plan business guard, plus Proposal & Fact Sheets, so a non-success run cannot be
    // persisted as complete.
    const isPlanExecutionReady = useMemo(() => {
        const status = adviceGoals?.global?.planExecutionStatus;
        const rebalancerResult = adviceGoals?.global?.planExecutionResponse?.rebalancerResult;
        const hasRebalancerResult = !!rebalancerResult && Object.keys(rebalancerResult).length > 0;
        return status === 'success' && hasRebalancerResult;
    }, [adviceGoals?.global?.planExecutionStatus, adviceGoals?.global?.planExecutionResponse]);

    const setRiskProfileFromVerticalScroller = async (selectedRisk: string) => {
        riskProfileFromVerticalScroller = selectedRisk;
        setHasRiskScrollerBeenTouched(true);
        const updatedAdviceClient = {
            ...adviceClient,
            riskProfile: riskProfileFromVerticalScroller
        };
        await dispatch(saveClient(updatedAdviceClient));
        if (checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603']) && adviceGoals?.global?.isPlanSavedOnce) {
            const riskChanged = adviceGoals?.request?.[0]?.riskProfile !== riskProfileFromVerticalScroller;
            dispatch(updatePlanContinuity({ isNewRiskProfile: riskChanged }));
        }
        // We empty the response, when risk value has changed. ref: AEX-2235
        if (
            adviceGoals?.global?.isPlanRun && // is subsequent journey
            adviceGoals?.request[0]?.riskProfile !== riskProfileFromVerticalScroller && // risk value has changed
            adviceGoals?.response?.length > 0 // has response based on old risk profile
        ) {
            setAdviceGoalsResponse(adviceGoals?.response); // caching old response
            dispatch(setAffectedEwsTerms(getEwsDurationKeys(Boolean(global?.globalConfig?.isMultiPortfolioTenant))));
            await dispatch(
                syncAdviceGoals({
                    ...adviceGoals,
                    response: [],
                    global: {
                        ...adviceGoals.global,
                        responseGoalCalculator: [],
                        status: STATUSVARIANTS.INCOMPLETE,
                        enablePDFGenerate: false
                    }
                })
            );
        } else if (
            adviceGoals?.global?.isPlanRun && // is subsequent journey
            adviceGoals?.request[0]?.riskProfile === riskProfileFromVerticalScroller && // risk value has reverted
            adviceGoalsResponse?.length > 0 // has cached response based on old risk profile
        ) {
            setAdviceGoalsResponse([]); // clearing cached response
            await dispatch(syncAdviceGoals({ ...adviceGoals, response: adviceGoalsResponse })); // restore cached response if risk profile is reverted back
        }
    };
    const PlanDetailsEnhancementFF = checkFFEnabled(FEATURE_NAMES['PLAN_DETAILS_ENHANCEMENT_FE_AEX-2554']);
    const lastStep = useRef<number>(0);
    const setLastStep = (num: any) => {
        lastStep.current = num;
    };
    const banner = adviceGoals?.global?.banner;
    // const isPlanRun = adviceGoals?.global?.isPlanRun;
    const bannerChangesExist = banner?.visible && banner.type === 'attention';
    const allFormFieldGoalIds = adviceGoals?.formFields?.map((goal) => goal.goalId);
    const allResponseGoalIds =
        adviceGoals?.global?.responseGoalCalculator
            ?.filter((goal) => !!goal?.response?.body)
            .map((goal) => goal.goalId) || [];
    const someGoalsHaveNoResponse = allFormFieldGoalIds.some((goalId: string) => !allResponseGoalIds.includes(goalId));
    const shouldRunPipe =
        lastStep.current == 3
            ? adviceGoals?.global?.enablePlanDetails ||
              adviceGoals.response?.length === 0 ||
              adviceGoals.response?.length !== adviceGoals.formFields?.length ||
              someGoalsHaveNoResponse ||
              bannerChangesExist
            : true;
    const { clientFirstName, clientLastName } = adviceClient;
    const clientFullName = `${clientFirstName}_${clientLastName}`;
    const goalDataRecomendation = adviceGoals?.recommendation?.find((rec: GoalRecommendationType) => {
        if (checkFFEnabled(FEATURE_NAMES['RECOMMENDATION_RECONCILIATION_FE_AEX-1848']))
            return (rec?.oneTimeTopUp || rec?.recomendedTenure || rec?.topUpAccumulation) && rec?.originalData;
    });

    interface GoalPortfolioMapping {
        goalName: string;
        portfolioName: string;
    }

    const getGoalPortfolioMappings = (adviceGoals: any, global: any): GoalPortfolioMapping[] => {
        if (!adviceGoals?.formFields || !adviceGoals?.response || !global?.portfolio) {
            return [];
        }

        return adviceGoals.formFields.map((goal: any) => {
            const goalResponse = adviceGoals?.response?.find((response: any) => response?.goalId === goal?.goalId);

            const portfolioId = goalResponse?.response?.body?.analysisReport?.recommendedPortfolioId;
            const portfolio = global?.portfolio?.find((p: any) => p.portfolioId === portfolioId);

            return {
                goalName: goal?.data?.goalName,
                portfolioName: portfolio?.portfolioName || '',
                portfolioUrl: portfolio?.portfolioUrl || ''
            };
        });
    };

    const goalPortfolioMappings = getGoalPortfolioMappings(adviceGoals, global);

    const handleInvalidUrlToaster = useCallback(() => {
        setFactSheetsMissingToast({ open: true, generating: false });
    }, []);

    useEffect(() => {
        const goalsMissingDates = adviceGoals?.formFields?.filter((goal) => {
            if (goal.data.goalType === GoalTypes.Retirement) {
                const retirementGoalData: any = goal.data;

                const isRetirementDateMissing =
                    !retirementGoalData.planStartRetirement ||
                    retirementGoalData.planStartRetirement === 'Invalid Date';
                const isPlanningDateMissing =
                    !retirementGoalData.targetDate || retirementGoalData.targetDate === 'Invalid Date';

                return isRetirementDateMissing || isPlanningDateMissing;
            }
            return false;
        });

        setGoalsWithMissingDates(goalsMissingDates || []);
    }, [adviceClient, adviceGoals]);

    // const isProfileNFinanceStepInErrorState = useMemo(() => {
    //     return (
    //         incomeWithMissingDetails?.length > 0 ||
    //         accountWithMissingDetails?.length > 0 ||
    //         profileWithMissingDetails?.length > 0
    //     );
    // }, [incomeWithMissingDetails, accountWithMissingDetails, profileWithMissingDetails]);

    const getBannerForMissingDates = (step: string, { updateStep }: ClientProfileProps) => {
        const showHandleTryAgain = step !== 'CLIENT_ONBOARDING_STEP2';
        const message = t('GOAL_DATE_ERROR_BANNER_MSG');
        return (
            <StyledBanner>
                <Banner
                    {...(showHandleTryAgain && {
                        handleTryAgain: async () => {
                            updateStep?.(2);
                            await dispatch(updateCurrentStep(2));
                        }
                    })}
                    type={'attention'}
                    message={message}
                />
            </StyledBanner>
        );
    };

    const updateGoalsCategoryForUnsavedGoals = async () => {
        // AEX-3108: single-portfolio tenants have no term categories to (re)compute.
        if (!global?.globalConfig?.isMultiPortfolioTenant) return;
        const language = global?.currentLanguage;
        // AEX-3466: only (re)categorize goals whose tenure isn't locked. isGoalTenureLocked treats a
        // stamped goal as locked and, for plans saved before stamping shipped, falls back to
        // isPlanSavedOnce so a saved plan's existing goals aren't recategorized on open/edit.
        const unsavedGoals = adviceGoals.formFields.filter(
            (goal) => goal.data.type !== GoalTypes.Retirement && !isGoalTenureLocked(goal.data, adviceGoals)
        );
        if (unsavedGoals.length === 0) return;
        const goals: GoalCategoryType[] = unsavedGoals.map((goal) => ({
            goalName: goal.data.goalName,
            goalTargetDate: dayjs(goal.data.targetDate, 'DD-MM-YYYY').format('DD-MM-YYYY')
        }));
        const data = {
            language: language || 'en',
            goals
        };
        try {
            dispatch(updateGlobalLoaderState(true));
            const response = await getGoalCategory(data);
            let goalCategoryHasChanged = false;
            const revisedGoals = adviceGoals.formFields.map((goal) => {
                const result = response?.goalCategoryResult?.[goal.data.goalName];
                if (result && 'label' in result && 'order' in result) {
                    if (goal.data.goalCategory !== result.value) {
                        goalCategoryHasChanged = true;
                    }
                    return {
                        ...goal,
                        data: {
                            ...goal.data,
                            goalCategory: result.value,
                            goalCategoryLabel: result.label,
                            goalCategoryOrder: result.order
                        }
                    };
                } else return goal;
            });
            if (goalCategoryHasChanged) await dispatch(saveGoal({ ...adviceGoals, formFields: revisedGoals }));
        } catch (error) {
            console.error('Error fetching goal category:', error);
        } finally {
            dispatch(updateGlobalLoaderState(false));
        }
    };

    useEffect(() => {
        // AEX-3466: run on open/edit; the call itself filters to goals not yet locked by a save, so a
        // never-saved plan categorizes all goals while a saved plan only categorizes a newly added one.
        if (
            (!adviceGoals?.global ||
                !adviceGoals?.global?.status ||
                adviceGoals?.global?.status === STATUSVARIANTS.INCOMPLETE) &&
            adviceGoals?.formFields?.length > 0
        )
            updateGoalsCategoryForUnsavedGoals();
    }, [adviceGoals?.global?.status]);

    // const getBannerForProfileMissingDetails = (step: string, { updateStep }: ClientProfileProps) => {
    //     const showHandleTryAgain = step !== 'CLIENT_ONBOARDING_STEP1_FF';
    //     const message = t('DETAILS_MISSING_BANNER_MSG');
    //     const linkLabel = t('REVIEW_PROFILE_AND_FINANCES');
    //     return (
    //         <StyledBanner>
    //             <Banner
    //                 {...(showHandleTryAgain && {
    //                     handleTryAgain: async () => {
    //                         updateStep?.(0);
    //                         await dispatch(updateCurrentStep(0));
    //                     }
    //                 })}
    //                 type={'attention'}
    //                 message={message}
    //                 hyperlinkLabelText={linkLabel}
    //             />
    //         </StyledBanner>
    //     );
    // };

    const calculateProbabilityOfSuccessForProposal = () => {
        const clonedAdviceGoals = cloneDeep(adviceGoals);
        let probabilityOfSuccessArray: number[] = [];
        // Restrict probability calculation to goals whose tenure has eligible funding sources.
        // Unfunded goals have no EWS/runpipe response, so including them would skew Math.min(...) to 0.
        const eligibleGoalIds = new Set(
            getEligibleGoals(
                adviceClient,
                clonedAdviceGoals,
                global?.orgInfo?.incomeSourceType?.en,
                global?.orgInfo?.investmentAccountType?.en
            ).map((g: any) => g?.goalId)
        );
        const modifiedAdviceGoalsFormFields = clonedAdviceGoals.formFields.map((item: any) => {
            if (!eligibleGoalIds.has(item.goalId)) {
                return item;
            }
            const goalJson = clonedAdviceGoals?.response?.find(
                (itemResponse: any) => item.goalId == itemResponse?.goalId
            );
            const analysisReport = goalJson?.['response']?.['body']?.['analysisReport'];

            const portfolio = global.portfolio?.filter(
                (_sPort) => _sPort.portfolioId === analysisReport?.['recommendedPortfolioId']
            );

            const groupedPortfolio = portfolio?.[0]?.assets?.map((_type: any) => ({
                type: _type.type,
                totalAllocatedvalue: `${_type.totalAllocatedvalue}%`
            }));
            // get equity fixed income
            const equityFixedIncomeCash = groupedPortfolio?.reduce(
                (
                    acc: { equity: number; fixedIncome: number; cash: number },
                    item: { type: string; totalAllocatedvalue: string }
                ) => {
                    if (item.type === 'Equity') {
                        acc.equity += parseFloat(item.totalAllocatedvalue);
                    } else if (item.type === 'Fixed Income') {
                        acc.fixedIncome += parseFloat(item.totalAllocatedvalue);
                    } else if (item.type === 'Cash') {
                        acc.cash += parseFloat(item.totalAllocatedvalue);
                    }
                    return acc;
                },
                { equity: 0, fixedIncome: 0, cash: 0 }
            );
            const equityFixedIncomeString = `${equityFixedIncomeCash?.equity} / ${equityFixedIncomeCash?.fixedIncome} / ${equityFixedIncomeCash?.cash}`;

            // get probability of success
            const probabilityOfSuccess = Math.round(Number(analysisReport?.['currentGoalProbability'] * 100));
            item.data.probabilityOfSuccess = probabilityOfSuccess;
            item.data.equityFixedIncome = analysisReport ? equityFixedIncomeString : '';
            // get updated recommendation values from request
            const recommendations = clonedAdviceGoals?.recommendation?.find((rec: any) => rec.goalId === item.goalId);
            const requestData = clonedAdviceGoals?.request?.find((req: any) => req.goalId === item.goalId) || {};
            if (recommendations?.topUpDecumulation) {
                item.data.targetedRetirementIncome = requestData.targetedRetirementIncome;
            }
            if (recommendations?.recomendedTenure) {
                if (item.data.goalType === 'retirement')
                    item.data.planStartRetirement = requestData?.planStartRetirement;
                else {
                    item.data.targetDate = requestData?.targetDate;
                    item.data.goalDate = requestData?.goalDate;
                }
            }
            probabilityOfSuccessArray.push(probabilityOfSuccess);
            delete item.data.createdAt;
            delete item.data.updatedAt;
            // if (adviceGoals.global.status === 'incomplete') item.data.update = false;
            return item;
        });
        // AEX-3466/AEX-3324: every goal saved with the plan is tenure-locked (incl. ineligible ones);
        // goals added afterwards stay unstamped until the next save, so they keep no tenure restriction.
        return {
            modifiedAdviceGoalsFormFields: stampGoalsTenureLocked(modifiedAdviceGoalsFormFields),
            probabilityOfSuccessArray
        };
    };

    const handleSavePlan = async () => {
        // Read the full EWS maps non-reactively at save time so the persisted document keeps the
        // legacy global.ewsRequests/ewsResponses shape without the component subscribing to them.
        const { requests: ewsRequestsState, responses: ewsResponsesState } = store.getState().ews ?? {
            requests: {},
            responses: {}
        };
        // Only persist a 'complete' plan when trade instructions (Rebalancer) are ready.
        if (!isPlanExecutionReady) {
            dispatch(setEnableConfirmConsoleNavigation(true));
            setSavePlanToast({ open: true, type: 'error' });
            return;
        }
        try {
            setSavePlanError(false);
            dispatch(showLoader());
            const { modifiedAdviceGoalsFormFields, probabilityOfSuccessArray } =
                calculateProbabilityOfSuccessForProposal();
            const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss.SSSSSS');
            const canEnablePDFGenerate = adviceGoals.global.banner?.type !== 'attention';
            const keysToRemove = ['hasUnsavedChanges', 'enableConfirmConsoleNavigation'];
            const profilePayload: any = { ...adviceClient };
            keysToRemove.forEach((key) => {
                if (key in profilePayload) delete profilePayload[key];
            });
            const updated_profilePayload = {
                ...profilePayload,
                accounts: {
                    ...profilePayload.accounts,
                    incomeSources: (profilePayload.accounts?.incomeSources || []).map(
                        // eslint-disable-next-line no-unused-vars
                        ({ updatedAt, createdAt, ...rest }: any) => {
                            if (!rest.accountId) return { ...rest };
                            return adviceGoals.global.status !== 'complete' ? { ...rest } : { ...rest, update: false };
                        }
                    ),
                    investmentsSources: (profilePayload.accounts?.investmentsSources || []).map(
                        // eslint-disable-next-line no-unused-vars
                        ({ updatedAt, createdAt, ...rest }: any) => {
                            if (!rest.accountId) return { ...rest };
                            return adviceGoals.global.status !== 'complete' ? { ...rest } : { ...rest, update: false };
                        }
                    )
                }
            };

            const updatedGlobal = {
                ...adviceGoals?.global,
                ewsRequests: ewsRequestsState,
                ewsResponses: ewsResponsesState,
                status: 'complete',
                ...(!adviceGoals.global.planCreationDate && { planCreationDate: timestamp }),
                lastUpdateDate: timestamp,
                enablePDFGenerate: canEnablePDFGenerate,
                probabilityOfSuccess: Math.min(...probabilityOfSuccessArray),
                enablePlanDetails: true,
                isPlanSavedOnce: true
            };

            const proposalsPayload = {
                _id: adviceGoals._id,
                apiName: adviceGoals.apiName || '',
                apiVer: adviceGoals.apiVer || '',
                assignedClientId: adviceGoals.assignedClientId || adviceClient._id,
                tenant: adviceGoals.tenant || 'advice',
                formFields: modifiedAdviceGoalsFormFields,
                isMultiGoal: adviceGoals.isMultiGoal || false,
                iwsApplied: adviceGoals.iwsApplied || false,
                goeConfigEmail: adviceGoals.goeConfigEmail || '',
                request: adviceGoals.request || [],
                response: adviceGoals.response || [],
                recommendation: adviceGoals.recommendation || [],
                global: { ...updatedGlobal }
            };
            if (proposalsPayload?.global?.planExecutionResponse) delete proposalsPayload.global.planExecutionResponse; // skip it since BE handles it
            const baseProfilePayload = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
                ? updated_profilePayload
                : profilePayload;
            // AEX-3363: nest the reference ids so the backend can reject a save made against a plan
            // that was changed in another tab (client_ref_id in profile, proposal_ref_id in proposals).
            const stalePlanEnabled = checkFFEnabled(FEATURE_NAMES['AEX-3342_ADD_VALIDATION_BEFORE_UPDATE']);
            const combinedPayload = {
                profile:
                    stalePlanEnabled && adviceClient?.client_ref_id
                        ? { ...baseProfilePayload, client_ref_id: adviceClient.client_ref_id }
                        : baseProfilePayload,
                proposals:
                    stalePlanEnabled && adviceGoals?.proposal_ref_id
                        ? { ...proposalsPayload, proposal_ref_id: adviceGoals.proposal_ref_id }
                        : proposalsPayload,
                events: adviceGoals?.events ?? [],
                notifications: adviceGoals?.notifications ?? [],
                tenant: tenant,
                language: i18n.language
            };
            const response = await mpSaveProposal(combinedPayload);
            if (response?.success === true) {
                dispatch(logEventToBackEnd('SAVE_PROPOSAL_SUCCESS'));
                // dispatch(setEnableConfirmConsoleNavigation(false));
                if (response?.profile?.client_ref_id) {
                    dispatch(
                        syncAdviceClient({
                            ...adviceClient,
                            client_ref_id: response.profile.client_ref_id,
                            enableConfirmConsoleNavigation: false
                        })
                    );
                }
                // Reflect the saved proposal/global state in Redux so post-save actions (Proposal &
                // Fact Sheets, Trade Instructions) become available without a refresh. Sync once with
                // the stamped formFields + updatedGlobal so the tenure lock (tenureLocked stamps and
                // isPlanSavedOnce) takes effect immediately on the Goals step — a second dispatch
                // spreading the stale pre-save adviceGoals used to revert them until a reload.
                dispatch(
                    syncAdviceGoals({
                        ...adviceGoals,
                        da_advice_id: response?.proposal?.da_advice_id,
                        proposal_ref_id: response?.proposal?.proposal_ref_id ?? adviceGoals.proposal_ref_id,
                        formFields: modifiedAdviceGoalsFormFields,
                        global: {
                            ...updatedGlobal,
                            enablePDFGenerate: true,
                            status: STATUSVARIANTS.COMPLETE,
                            isPlanSavedOnce: true
                        }
                    })
                );
                // The saved plan now carries every change that was kept in the store.
                dispatch(clearUnsavedPlanChanges());

                if (checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603'])) {
                    dispatch(
                        seedAdviceContinuityForReallocation({
                            goals: (modifiedAdviceGoalsFormFields ?? []).map((goal: any) => ({
                                goalId: goal.goalId,
                                currentPortfolioId: null
                            })),
                            isPlanSavedOnce: true
                        })
                    );
                }
                setSavePlanToast({ open: true, type: 'confirm' });
            } else {
                dispatch(setEnableConfirmConsoleNavigation(true));
                setSavePlanToast({ open: true, type: 'error' });
                setSavePlanError(true);
            }
        } catch (error) {
            console.error('Error in handleSavePlan:', error);
            dispatch(logEventToBackEnd('SAVE_PROPOSAL_ERROR'));
            dispatch(setEnableConfirmConsoleNavigation(true));
            // AEX-3363: a stale-plan rejection shows the Refresh Plan banner instead of the generic
            // save-failed error, so the advisor can re-fetch and re-apply on top of the latest plan.
            if (!markStaleFromError(error)) {
                setSavePlanToast({ open: true, type: 'error' });
                setSavePlanError(true);
            }
        } finally {
            dispatch(removeLoader());
        }
    };

    const triggerPlanExecution = async (forceRefresh = false) => {
        if (forceRefresh || !isPlanExecutionReady) {
            dispatch(showLoader());
            if (goalCategoryPortfolioMappings) {
                await callPlanExecution(
                    adviceGoals,
                    adviceClient,
                    goalCategoryPortfolioMappings,
                    dispatch,
                    forceRefresh
                );
            } else {
                console.error(
                    'Goal category portfolio mappings are not available. Plan execution cannot be triggered.'
                );
            }
            dispatch(removeLoader());
        }
    };

    // Validation function to check if client can continue to next step
    const isClientValidForNextStep = (adviceClient: any) => {
        const hasFirstName = adviceClient?.clientFirstName && adviceClient.clientFirstName.trim() !== '';
        const hasLastName = adviceClient?.clientLastName && adviceClient.clientLastName.trim() !== '';

        // Check if either income sources or investment sources have items
        const hasIncomeSources = adviceClient?.accounts?.incomeSources?.length > 0;
        const hasInvestmentSources = adviceClient?.accounts?.investmentsSources?.length > 0;

        // Return true only if all conditions are met
        return hasFirstName && hasLastName && (hasIncomeSources || hasInvestmentSources);
    };

    // Validation function for risk tolerance step
    const isRiskToleranceStepValid = (adviceClient: any) => {
        return !!(
            riskProfileFromVerticalScroller ||
            (adviceClient?.riskProfile && adviceClient.riskProfile.trim() !== '') ||
            adviceClient?.step > 1
        );
    };

    // handling continue to next step button click from risk tolerance step
    const handleSyncRiskTolerance = async () => {
        // Create unique stages array
        const existingStages = adviceClient?.stages || [];
        const newStages = [...existingStages, CLIENT_PROFILE_STAGE_KEYS[2], CLIENT_PROFILE_STAGE_KEYS[3]];
        const uniqueStages = Array.from(new Set(newStages));

        // API call to persist riskProfile and stages
        const updatedAdviceClient = {
            ...adviceClient,
            riskProfile: riskProfileFromVerticalScroller,
            stages: uniqueStages,
            step: adviceClient.step + 1
        };
        await dispatch(saveClient(applyAccountFieldStripping(updatedAdviceClient, adviceGoals?.global?.isPlanRun)));
        riskPersistedInBackendRef.current = true;

        // Reset goals response if it exists
        if (adviceGoals?.response?.length > 0) {
            let goalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
            goalsData.response = [];
            goalsData.global = {
                ...goalsData.global,
                status: STATUSVARIANTS.INCOMPLETE,
                enablePDFGenerate: false,
                enablePlanDetails: false,
                responseGoalCalculator: []
            };
            dispatch(setAffectedEwsTerms(getEwsDurationKeys(Boolean(global?.globalConfig?.isMultiPortfolioTenant))));
            await dispatch(saveGoal(goalsData));
        }
    };

    const saveClientIfPayloadChanged = async (nextStep: number) => {
        const isPlanRun = adviceGoals?.global?.isPlanRun;
        const currentClientPayload = applyAccountFieldStripping(adviceClient, isPlanRun);
        const nextClientPayload = applyAccountFieldStripping({ ...adviceClient, step: nextStep }, isPlanRun);
        const nextPayloadWithoutStep = { ...nextClientPayload, step: currentClientPayload.step };
        // Force a save when entering step 2 while risk isn't persisted in the backend yet. We rely
        // on the backend-seeded ref (not the live `adviceClient`, which already shows the default
        // risk profile) to make this decision.
        const forceRiskSave = nextStep === 2 && riskPersistedInBackendRef.current !== true;
        if (!isEqual(currentClientPayload, nextPayloadWithoutStep) || forceRiskSave) {
            await dispatch(saveClient(nextClientPayload));
            riskPersistedInBackendRef.current = true;
        }
    };

    // Usage example with your existing ContinueToNextStep component:
    const ContinueToNextStep = ({ updateStep, disabled }: ClientProfileProps) => {
        // Subscribe to the Accounts/Income edit-open flag HERE (leaf button), not in the journey
        // body: toggling it then re-renders only this button and does NOT remount the inline
        // Accounts table, which keeps its edit state internally (a remount would cancel the edit).
        const hasOpenAccountsEdit = useAppSelector((state) => state.profileEdit?.hasOpenAccountsEdit ?? false);
        return (
            <ContinueStepButton
                variant="contained"
                onClick={async () => {
                    const proceed = async () => {
                        updateStep?.((prev) => prev + 1);
                        // update the step in redux
                        await dispatch(updateCurrentStep(adviceClient.step + 1));
                        if (!adviceGoals?.global?.isPlanRun) {
                            await saveClientIfPayloadChanged(adviceClient.step + 1);
                        }
                    };
                    const runContinue = async () => {
                        if (
                            adviceClient.step === 0 &&
                            !isAccountIncomeMappingEnabled &&
                            shouldPromptForIncome(adviceClient)
                        ) {
                            openIncomeConfirmModal(proceed);
                            return;
                        }
                        if (adviceClient.step === 0 && shouldPromptForAccounts(adviceClient)) {
                            openAccountConfirmModal(proceed);
                            return;
                        }
                        await proceed();
                    };
                    if (guardProfileLeave(runContinue)) return;
                    if (tabIndex === '1' && guardAccountsLeave(runContinue)) return;
                    await runContinue();
                }}
                disabled={disabled || hasOpenAccountsEdit}
            >
                {t('CLIENT_ONBOARDING_CONTD_BTN')}
            </ContinueStepButton>
        );
    };

    // Continue button for risk tolerance step
    const ContinueFromRiskToleranceStep = ({ updateStep, disabled }: ClientProfileProps) => {
        const isValidForNextStep = isRiskToleranceStepValid(adviceClient) && isClientValidForNextStep(adviceClient);

        return (
            <ContinueStepButton
                variant="contained"
                onClick={async () => {
                    updateStep?.((prev) => prev + 1);
                    // Only sync risk tolerance if the vertical scroller was touched
                    if (hasRiskScrollerBeenTouched) {
                        handleSyncRiskTolerance();
                    } else {
                        await dispatch(updateCurrentStep(adviceClient.step + 1));
                        if (!adviceGoals?.global?.isPlanRun) {
                            await saveClientIfPayloadChanged(adviceClient.step + 1);
                        }
                    }
                }}
                disabled={disabled || !isValidForNextStep}
            >
                {t('CLIENT_ONBOARDING_CONTD_BTN')}
            </ContinueStepButton>
        );
    };

    const openIncomeConfirmModal = (onProceed: () => void | Promise<void>) => {
        setConfirmModal({
            title: t('PROCEED_WITHOUT_INCOME_TITLE'),
            content: t('PROCEED_WITHOUT_INCOME_CONTENT'),
            confirmLabel: t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT'),
            cancelLabel: t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT'),
            onProceed
        });
    };

    const openAccountConfirmModal = (onProceed: () => void | Promise<void>) => {
        setConfirmModal({
            title: t('PROCEED_WITHOUT_ACCOUNTS_TITLE'),
            content: t('PROCEED_WITHOUT_ACCOUNTS_CONTENT'),
            confirmLabel: t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT'),
            cancelLabel: t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT'),
            onProceed
        });
    };

    const handleConfirmModalConfirm = async () => {
        const onProceed = confirmModal?.onProceed;
        setConfirmModal(null);
        if (onProceed) await onProceed();
    };

    const handleConfirmModalCancel = () => {
        setConfirmModal(null);
    };

    const proceedToFundingNavigator = async (updateStep?: ClientProfileProps['updateStep']) => {
        // make api call to api/advice/ews - callSelectiveEWS
        // now replaced with callUnifiedEWS
        if (checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_EWS_STREAMING_FE_AEX-3495'])) {
            callUnifiedEWS(
                dispatch,
                global?.orgInfo?.incomeSourceType?.en,
                global?.orgInfo?.investmentAccountType?.en,
                adviceClient,
                adviceGoals,
                global?.orgInfo?.orgEmail || '',
                tenant,
                goalCategoryPortfolioMappings,
                global?.goeConfig?.reallocSchedules
            );
        } else {
            callSelectiveEWS(
                dispatch,
                global?.orgInfo?.incomeSourceType?.en,
                global?.orgInfo?.investmentAccountType?.en,
                adviceClient,
                adviceGoals,
                global?.orgInfo?.orgEmail || '',
                tenant,
                goalCategoryPortfolioMappings,
                global?.goeConfig?.reallocSchedules
            );
        }
        updateStep?.((prev) => prev + 1);
        // update the step in redux
        await dispatch(updateCurrentStep(adviceClient.step + 1));
        setLastStep(0);
    };

    const buildFundingMissingContent = (tenures: EwsDuration[]) => {
        if (tenures.length === 0) return '';
        if (tenures.includes(DEFAULT_GOAL_CATEGORY)) return t('FUNDING_SOURCES_MISSING_CONTENT_DEFAULT');
        // Reuse existing SHORT/MEDIUM/LONG i18n keys via EWS_DURATION_KEYS:
        // 'Short Term' -> 'shortTerm' -> 'SHORT'
        const labels = tenures.map((d) => t(EWS_DURATION_KEYS[d].replace('Term', '').toUpperCase()));
        // Intl.ListFormat handles locale-specific conjunction rules
        // (e.g. English Oxford comma "A, B, and C" vs Spanish "A, B y C").
        const tenuresText = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(labels);
        return t('FUNDING_SOURCES_MISSING_CONTENT', { tenures: tenuresText });
    };

    const tryProceedToFundingNavigator = (onProceed: () => void | Promise<void>): boolean => {
        const tenures = getMissingFundingTenures(
            adviceClient,
            adviceGoals,
            global?.orgInfo?.incomeSourceType?.en,
            global?.orgInfo?.investmentAccountType?.en
        );
        if (tenures.length > 0) {
            setConfirmModal({
                title: t('FUNDING_SOURCES_MISSING_TITLE'),
                content: buildFundingMissingContent(tenures),
                confirmLabel: t('CONTINUE_TEXT'),
                cancelLabel: t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT'),
                cancelButtonFirst: true, // Per design, the funding-missing modal shows Cancel first
                onProceed
            });
            return true;
        }
        return false;
    };

    const ContinueToFundingStep = ({ updateStep, disabled }: ClientProfileProps) => (
        <ContinueStepButton
            variant="contained"
            onClick={async () => {
                const proceed = () => proceedToFundingNavigator(updateStep);
                if (tryProceedToFundingNavigator(proceed)) return;
                await proceed();
            }}
            disabled={disabled}
        >
            {t('CLIENT_ONBOARDING_CONTD_BTN')}
        </ContinueStepButton>
    );

    const ContinueToReviewStep = ({ updateStep, disabled }: ClientProfileProps) => (
        <ContinueStepButton
            variant="contained"
            onClick={async () => {
                callMpGoalCalculator(
                    dispatch,
                    adviceGoals,
                    global?.orgInfo?.orgEmail || '',
                    goalCategoryPortfolioMappings
                );
                updateStep?.((prev) => prev + 1);
                // update the step in redux
                await dispatch(updateCurrentStep(adviceClient.step + 1));
                setLastStep(0);
            }}
            disabled={disabled}
        >
            {t('CLIENT_ONBOARDING_CONTD_BTN')}
        </ContinueStepButton>
    );

    const ContinueToPlanExecutionStep = ({ updateStep, disabled }: ClientProfileProps) => (
        <ContinueStepButton
            variant="contained"
            onClick={async () => {
                dispatch(updateGlobalLoaderState(true));
                await triggerPlanExecution();
                dispatch(updateGlobalLoaderState(false));
                updateStep?.((prev) => prev + 1);
                // update the step in redux
                await dispatch(updateCurrentStep(adviceClient.step + 1));
                setLastStep(0);
            }}
            disabled={disabled}
        >
            {t('CLIENT_ONBOARDING_CONTD_BTN')}
        </ContinueStepButton>
    );

    // Initializing Stepper steps
    const CreateProfile = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                {/* <StyledBanner>
                    {!isProfileNFinanceStepInErrorState &&
                        goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_ONBOARDING_STEP1_FF', { updateStep })}
                    {isProfileNFinanceStepInErrorState &&
                        getBannerForProfileMissingDetails('CLIENT_ONBOARDING_STEP1_FF', { updateStep })}
                </StyledBanner> */}
                <Grid container justifyContent="space-between" alignItems="center" padding="32px 0">
                    <Typography
                        variant="headers.h2"
                        color="semantic.text.brand"
                        t={'CLIENT_ONBOARDING_STEP1_FF'}
                        sx={{ fontWeight: 700, lineHeight: '38px' }}
                    ></Typography>
                    <ContinueToNextStep
                        updateStep={updateStep}
                        // Edit-mode lock (AEX-3445): disable while creating/editing client or co-client.
                        // The account/income edit-open case is ORed in inside ContinueToNextStep.
                        disabled={!isClientValidForNextStep(adviceClient) || profileDraftActive}
                    />
                </Grid>
            </StickyRegion>
            <CreateClientStep
                tabIndex={tabIndex}
                setTabIndex={setTabIndex}
                openClientDrawer={openClientDrawer}
                setOpenClientDrawer={setOpenClientDrawer}
                isRelationship={isRelationship}
                setIsRelationship={setIsRelationship}
                profileDraftActive={profileDraftActive}
                profileDraftBlocks={profileDraftBlocks}
                planHasDetailsBeyondProfile={planHasDetailsBeyondProfile}
                requestProfileLeave={guardProfileLeave}
                requestAccountsLeave={guardAccountsLeave}
                editingRowKey={profileEditingRowKey}
                onEditingRowKeyChange={setProfileEditingRowKey}
                isAddingNew={profileIsAddingNew}
                onAddCoClient={() => setProfileAddingCoClient(true)}
                onCancelAdd={() => setProfileAddingCoClient(false)}
            />
        </div>
    );

    const RiskTolerance = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {/* {
                    goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_PROFILE_RISK_TOLERANCE_HEADING', { updateStep })} */}
                </StyledBanner>
                <Grid container justifyContent="space-between" alignItems="center" padding="16px 0">
                    <Typography
                        variant="headers.h2"
                        color="semantic.text.brand"
                        sx={{ fontWeight: 700, lineHeight: '38px' }}
                        t="CLIENT_PROFILE_RISK_TOLERANCE_HEADING"
                    ></Typography>
                    <ContinueFromRiskToleranceStep updateStep={updateStep} />
                </Grid>
            </StickyRegion>
            <RiskToleranceStep
                clientName={clientName}
                riskOptions={riskOptions}
                selectedRiskProfile={riskProfileFromVerticalScroller}
                updateRisk={setRiskProfileFromVerticalScroller}
                adviceClientRiskProfile={adviceClient?.riskProfile}
                updateAdviceClientRiskProfile={async () => {
                    const updatedAdviceClient = {
                        ...adviceClient,
                        riskProfile: riskProfileFromVerticalScroller
                    };
                    await dispatch(syncAdviceClient(updatedAdviceClient));
                }}
                minMaxEquity={minMaxEquity}
            />
        </div>
    );

    const CreateGoals = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_ONBOARDING_STEP2', { updateStep })}
                </StyledBanner>
                <Grid container justifyContent="space-between" padding="16px 0" alignItems="center">
                    <Typography
                        variant="headers.h2"
                        color="semantic.text.brand"
                        sx={{ fontWeight: 700, lineHeight: '38px' }}
                        t="CLIENT_ONBOARDING_STEP2"
                    ></Typography>
                    <ContinueToFundingStep
                        updateStep={updateStep}
                        disabled={
                            !adviceGoals?.formFields ||
                            adviceGoals?.formFields?.length < 1 ||
                            goalsWithMissingDates?.length > 0 ||
                            hasPastGoalDates ||
                            hasUnsavedGoalChanges
                        }
                    />
                </Grid>
            </StickyRegion>
            <CreateGoalsStep goalsWithMissingDates={goalsWithMissingDates} />
        </div>
    );

    const renderFundingNavigator = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_ONBOARDING_STEP3', { updateStep })}
                </StyledBanner>
                <Grid container justifyContent="space-between" padding="16px 0 32px" alignItems="center">
                    <Typography
                        variant="headers.h2"
                        color="semantic.text.brand"
                        sx={{ fontWeight: 700, lineHeight: '38px' }}
                        t="CLIENT_ONBOARDING_STEP3"
                    ></Typography>
                    <ContinueToReviewStep
                        updateStep={updateStep}
                        disabled={
                            !adviceGoals?.formFields ||
                            adviceGoals?.formFields?.length < 1 ||
                            goalsWithMissingDates?.length > 0 ||
                            hasPastGoalDates ||
                            // isProfileNFinanceStepInErrorState ||
                            hasMissingFundingTenures ||
                            // Disable while the unified EWS stream is in flight.
                            ewsLoading ||
                            //if any one of terms is in loading state or error state then disable continue button
                            ewsStepperCtaStatus.anyLoading ||
                            ewsStepperCtaStatus.anyError ||
                            hasNoEligibleFunds
                        }
                    />
                </Grid>
            </StickyRegion>
            <FundingNavigatorStep />
        </div>
    );

    const renderReviewPlanDetails = ({ updateStep }: ClientProfileProps) => {
        return (
            <div style={{ maxWidth: '100%' }}>
                <StickyRegion>
                    <StyledBanner>
                        {/* {goalsWithMissingDates?.length > 0
                            ? getBannerForMissingDates('CLIENT_ONBOARDING_STEP4', { updateStep })
                            : isPlanRun &&
                              banner &&
                              banner.visible && (
                                  <Banner
                                      type={banner.type || 'attention'}
                                      handleClose={() => {
                                          dispatch(updateBanner({ visible: false }));
                                      }}
                                      message={banner.message}
                                  />
                              )} */}
                    </StyledBanner>
                    <Grid container justifyContent="space-between" padding="16px 0" alignItems="center">
                        <StyleDivWithStatus>
                            <Typography
                                variant="headers.h2"
                                color="semantic.text.brand"
                                sx={{ fontWeight: 700, lineHeight: '38px' }}
                                t="CLIENT_ONBOARDING_STEP4"
                            ></Typography>
                            <InfoTooltip
                                message={
                                    adviceGoals?.global?.status === STATUSVARIANTS.COMPLETE
                                        ? t('STATUS_PILL_COMPLETE_TEXT')
                                        : t('STATUS_PILL_INCOMPLETE_TEXT')
                                }
                                placement="bottom-start"
                            >
                                <StatusChip>
                                    {adviceGoals?.global?.status === STATUSVARIANTS.COMPLETE
                                        ? t('COMPLETE_TEXT')
                                        : t('INCOMPLETE_DRAFT_TEXT')}
                                </StatusChip>
                            </InfoTooltip>
                        </StyleDivWithStatus>
                        <ContinueToPlanExecutionStep
                            updateStep={updateStep}
                            disabled={
                                (goalsWithMissingDates && goalsWithMissingDates?.length > 0) ||
                                hasPastGoalDates ||
                                (affectedEwsTerms?.length ?? 0) > 0 ||
                                !(adviceGoals?.global?.responseGoalCalculator?.length ?? 0) ||
                                adviceGoals?.global?.responseGoalCalculator?.some(
                                    (responseGC) => responseGC?.loading || responseGC?.error
                                ) ||
                                hasMissingFundingTenures
                            }
                        />
                    </Grid>
                </StickyRegion>
                {PlanDetailsEnhancementFF ? (
                    <PlanOverview />
                ) : (
                    <ReviewPlanDetailsStep updateStep={updateStep} callRunpipe={callRunpipe} />
                )}
            </div>
        );
    };

    // Revisit to optimize when Risk Tolerance step is included
    const checkStepDisabled = (stepNumber: number) => {
        if (stepNumber === adviceClient.step) return true;
        // AEX-3430: block every step beyond Goals while any goal has a past timeline date, so the
        // advisor must return to the Goals step and fix the dates before the plan can progress.
        if (hasPastGoalDates && stepNumber > 2) return true;
        if (!adviceGoals?.global?.isPlanRun && stepNumber === 0 && !isClientValidForNextStep(adviceClient)) {
            return true;
        }
        if (
            !adviceGoals?.global?.isPlanRun &&
            stepNumber === 1 &&
            (!isRiskToleranceStepValid(adviceClient) || !isClientValidForNextStep(adviceClient))
        ) {
            return true;
        }
        if (
            !adviceGoals?.global?.isPlanRun &&
            stepNumber === 2 &&
            (!adviceClient?.riskProfile || !isClientValidForNextStep(adviceClient))
        )
            return true;
        if (adviceClient?.step < 3 && stepNumber === 3 && adviceGoals?.formFields?.length === 0) return true;
        if (
            stepNumber === 4 &&
            (adviceGoals.formFields.length === 0 ||
                hasMissingFundingTenures ||
                hasNoEligibleFunds ||
                (goalsWithMissingDates && goalsWithMissingDates?.length > 0) ||
                (affectedEwsTerms?.length ?? 0) > 0 ||
                ewsLoading ||
                !ewsStepperCtaStatus.allTermsReady)
        )
            return true;
        if (stepNumber === 4 && (shouldRunPipe || someGoalsHaveNoResponse)) {
            return false;
        }

        if (
            stepNumber === 5 &&
            (hasMissingFundingTenures ||
                affectedEwsTerms?.length ||
                !adviceGoals?.global?.responseGoalCalculator?.length ||
                adviceGoals?.global?.responseGoalCalculator?.some(
                    (responseGC) => responseGC?.loading || responseGC?.error
                ))
        )
            return true;
    };

    // StepperPillsProps fixes onStepClick at (stepNumber) => void, so we use these refs to bypass
    // a prompt block exactly once when re-entering onStepClick after the user confirms it.
    // They are intentionally separate so confirming the income/accounts prompt does NOT also
    // suppress the funding prompt — e.g. on a 0→3 jump both modals must be shown in sequence.
    const skipIncomeAccountsPromptRef = useRef(false);
    const skipFundingPromptRef = useRef(false);
    const skipProfileLeavePromptRef = useRef(false);
    const skipAccountsLeavePromptRef = useRef(false);
    const skipGoalsLeavePromptRef = useRef(false);

    const clearProfileDraft = useCallback(() => {
        setProfileEditingRowKey(null);
        setProfileAddingCoClient(false);
        dispatch(setHasUnsavedProfileChanges(false));
    }, [dispatch]);

    const guardProfileLeave = useCallback(
        (proceed: () => void | Promise<void>): boolean => {
            if (skipProfileLeavePromptRef.current) {
                skipProfileLeavePromptRef.current = false;
                return false;
            }
            if (!profileDraftActive || !planHasDetailsBeyondProfile) return false;
            const hasUnsavedProfileChanges = store.getState().profileEdit?.hasUnsavedProfileChanges;
            if (hasUnsavedProfileChanges) {
                leaveModalRef.current?.open(async () => {
                    clearProfileDraft();
                    await proceed();
                });
                return true;
            }
            clearProfileDraft();
            return false;
        },
        [profileDraftActive, planHasDetailsBeyondProfile, store, clearProfileDraft]
    );

    // Accounts/Income tab counterpart of guardProfileLeave: when the inline Accounts table has an
    // in-progress add/edit with actual (dirty) changes, defer navigation behind the same Stay/Leave
    // confirmation. The dirty signal is the shared `profileEdit.hasUnsavedProfileChanges` flag,
    // which AccountsInlineTable sets via onDirtyStateChange. Returns true when the modal was opened.
    const guardAccountsLeave = useCallback(
        (proceed: () => void | Promise<void>): boolean => {
            if (skipAccountsLeavePromptRef.current) {
                skipAccountsLeavePromptRef.current = false;
                return false;
            }
            if (!isAccountIncomeMappingEnabled) return false;
            const hasUnsavedAccountChanges = store.getState().profileEdit?.hasUnsavedProfileChanges;
            if (!hasUnsavedAccountChanges) return false;
            leaveModalRef.current?.open(async () => {
                dispatch(setHasUnsavedProfileChanges(false));
                await proceed();
            });
            return true;
        },
        [isAccountIncomeMappingEnabled, store, dispatch]
    );

    // Goals-step counterpart of guardProfileLeave: when an inline goal row is open (add/edit), defer
    // stepper navigation behind the Stay/Leave confirmation. On "Leave" the in-progress goal edit is
    // discarded (clearGoalEdit) and navigation proceeds. Returns true when the modal was opened.
    const guardGoalsLeave = useCallback(
        (proceed: () => void | Promise<void>): boolean => {
            if (skipGoalsLeavePromptRef.current) {
                skipGoalsLeavePromptRef.current = false;
                return false;
            }
            if (!hasUnsavedGoalChanges) return false;
            // Only prompt when the open row has actual (dirty) changes; an untouched edit is
            // discarded silently, matching the in-table cancel confirmation.
            const hasUnsavedGoalEdits = store.getState().goalEdit?.hasUnsavedGoalEdits;
            if (hasUnsavedGoalEdits) {
                leaveModalRef.current?.open(async () => {
                    dispatch(clearGoalEdit());
                    await proceed();
                });
                return true;
            }
            dispatch(clearGoalEdit());
            return false;
        },
        [hasUnsavedGoalChanges, store, dispatch]
    );

    const triggerBackendDownload = async () => {
        await downloadPdfThroughBackend(
            {
                tenant: tenant || '',
                proposalId: adviceGoals?._id || '',
                assignedClientId: adviceClient?._id || '',
                da_advice_id: adviceGoals?.da_advice_id || '',
                orgEmail: global?.orgInfo?.orgEmail || '',
                weightType: weightTypes || '',
                filename: `${adviceClient?.clientFirstName ?? ''}_${
                    adviceClient?.clientLastName ?? ''
                }_${dayjs().format('MMDDYY')}.pdf`,
                language: i18n.language
            },
            setPdfGenerateToast
        );
    };

    const journeySteps: StepperPillsProps = {
        steps: [
            {
                id: ADVISOR_ROUTES.CLIENT_PROFILE,
                label: t('CLIENT_ONBOARDING_STEP1_FF'),
                icon: <AccountIcon />,
                status: isClientValidForNextStep(adviceClient) ? 'completed' : ('pending' as const),
                content: <CreateProfile />,
                disabled: checkStepDisabled(0)
            },
            {
                id: ADVISOR_ROUTES.RISK_TOLERANCE,
                label: t('CLIENT_PROFILE_RISK_TOLERANCE_HEADING'),
                icon: <RiskToleranceIcon />,
                status: adviceClient.step > 0 || adviceClient?.riskProfile ? 'completed' : ('pending' as const),
                content: <RiskTolerance />,
                disabled: checkStepDisabled(1)
            },
            {
                id: ADVISOR_ROUTES.GOALS,
                label: t('CLIENT_ONBOARDING_STEP2'),
                icon: <GoalsIcon />,
                status:
                    (goalsWithMissingDates && goalsWithMissingDates?.length > 0) || hasPastGoalDates
                        ? 'error'
                        : adviceGoals?.formFields?.length !== 0
                        ? 'completed'
                        : ('pending' as const),
                content: <CreateGoals />,
                disabled: adviceClient.step === 1 ? false : checkStepDisabled(2)
            },
            {
                id: ADVISOR_ROUTES.FUNDING_NAVIGATOR,
                label: t('CLIENT_ONBOARDING_STEP3'),
                icon: <FundingNavigatorIcon />,
                status:
                    (adviceGoals?.formFields?.length !== 0 &&
                        !(affectedEwsTerms?.length ?? 0) &&
                        ewsStepperCtaStatus.hasAnyTerm &&
                        !hasMissingFundingTenures &&
                        ewsStepperCtaStatus.allTermsReady &&
                        !ewsLoading) ||
                    adviceClient?.step > 3
                        ? 'completed'
                        : ('pending' as const),
                content: <StepContent render={renderFundingNavigator} />,
                disabled: checkStepDisabled(3)
            },
            {
                id: ADVISOR_ROUTES.PLAN_DETAILS,
                label: t('CLIENT_ONBOARDING_STEP4'),
                icon: <PlanDetailsIcon />,
                status:
                    ewsStepperCtaStatus.allTermsReady &&
                    goalsWithMissingDates?.length === 0 &&
                    affectedEwsTerms?.length === 0 &&
                    adviceGoals?.global?.responseGoalCalculator &&
                    adviceGoals?.global?.responseGoalCalculator?.length > 0 &&
                    adviceGoals?.global?.responseGoalCalculator?.every(
                        (responseGC) => !responseGC?.loading && !responseGC?.error
                    ) &&
                    !hasMissingFundingTenures
                        ? 'completed'
                        : adviceClient.step === 3 &&
                          adviceGoals?.formFields?.length > 0 &&
                          goalsWithMissingDates?.length === 0
                        ? 'pending'
                        : ('pending' as const),
                content: <StepContent render={renderReviewPlanDetails} />,
                disabled: checkStepDisabled(4)
            },
            {
                id: ADVISOR_ROUTES.PLAN_EXECUTION,
                label: t('CLIENT_ONBOARDING_STEP6'),
                icon: <PlanExecutionIcon />,
                status:
                    Object.values(adviceGoals?.global?.planExecutionResponse?.rebalancerResult || [])?.length > 0 &&
                    affectedEwsTerms?.length === 0 &&
                    adviceGoals?.global?.responseGoalCalculator?.length &&
                    adviceGoals?.global?.responseGoalCalculator?.every(
                        (responseGC) => !responseGC?.loading && !responseGC?.error
                    ) &&
                    !hasMissingFundingTenures
                        ? 'completed'
                        : ('pending' as const),
                content: (
                    <PlanExecutionSection
                        tactical={tactical}
                        strategic={strategic}
                        weightTypes={weightTypes}
                        isPlanExecutionReady={isPlanExecutionReady}
                        goalsWithMissingDates={goalsWithMissingDates}
                        goalDataRecomendation={goalDataRecomendation}
                        savePlanError={savePlanError}
                        isStalePlan={isStalePlan}
                        onRefreshPlan={refreshPlan}
                        supportEmail={supportEmail}
                        openPDFModal={openPDFModal}
                        setOpenPDFModal={setOpenPDFModal}
                        setFactSheetsMissingToast={setFactSheetsMissingToast}
                        setPdfGenerateToast={setPdfGenerateToast}
                        triggerBackendDownload={triggerBackendDownload}
                        handleSavePlan={handleSavePlan}
                        triggerPlanExecution={triggerPlanExecution}
                    />
                ),
                disabled: checkStepDisabled(5)
            }
        ],
        currentStep: adviceClient.step,
        setLastStep: setLastStep,
        onStepClick: async (stepNumber) => {
            setLastStep(adviceClient.step);
            // if (stepNumber === 5) return;
            if (profileDraftActive && adviceClient.step === 0 && stepNumber !== 0) {
                // Brand-new, profile-only plan: keep blocking navigation while adding/editing.
                if (!planHasDetailsBeyondProfile) return;
                // Plan with other details: confirm (Stay/Leave) before leaving the draft. On "Leave"
                // the draft is discarded and this handler is re-invoked with the bypass flag set.
                if (
                    guardProfileLeave(() => {
                        skipProfileLeavePromptRef.current = true;
                        return journeySteps.onStepClick?.(stepNumber);
                    })
                )
                    return;
            }
            // Accounts/Income tab draft with unsaved changes: confirm (Stay/Leave) before leaving
            // Step 1 via the stepper. On "Leave" the flag is cleared and this handler re-runs with
            // the bypass flag set.
            if (isAccountIncomeMappingEnabled && tabIndex === '1' && adviceClient.step === 0 && stepNumber !== 0) {
                if (
                    guardAccountsLeave(() => {
                        skipAccountsLeavePromptRef.current = true;
                        return journeySteps.onStepClick?.(stepNumber);
                    })
                )
                    return;
            }
            // Goals inline edit (AEX-3445): confirm (Stay/Leave) before leaving the Goals step via
            // the stepper. Gate on an enabled target step because onStepClick also fires for disabled
            // pills; guardGoalsLeave is a no-op when no goal row is open. On "Leave" the draft is
            // discarded and this handler re-runs with the bypass flag set.
            if (stepNumber !== GOALS_STEP_INDEX && checkStepDisabled(stepNumber) !== true) {
                if (
                    guardGoalsLeave(() => {
                        skipGoalsLeavePromptRef.current = true;
                        return journeySteps.onStepClick?.(stepNumber);
                    })
                )
                    return;
            }
            if (checkStepDisabled(stepNumber) === true) return;
            // Consume each skip flag only at its own guard so one confirmed prompt
            // does not clear the other during the 0->3 income/accounts -> funding chain.
            if (adviceClient.step === 0 && stepNumber !== 0) {
                const skipIncomeAccountsPrompt = skipIncomeAccountsPromptRef.current;
                skipIncomeAccountsPromptRef.current = false;
                if (!skipIncomeAccountsPrompt) {
                    const proceed = () => {
                        skipIncomeAccountsPromptRef.current = true;
                        return journeySteps.onStepClick?.(stepNumber);
                    };
                    if (shouldPromptForAccounts(adviceClient)) {
                        openAccountConfirmModal(proceed);
                        return;
                    }
                }
            }
            // Guard step-3 navigation with the funding-missing prompt. Placed before the
            // catch-all branch below (which would otherwise swallow this case as dead code).
            const skipFundingPrompt = skipFundingPromptRef.current;
            skipFundingPromptRef.current = false;
            if (
                !skipFundingPrompt &&
                stepNumber === 3 &&
                adviceClient?.step < 3 &&
                adviceGoals?.formFields?.length > 0 &&
                !checkStepDisabled(stepNumber)
            ) {
                const fundingProceed = async () => {
                    skipFundingPromptRef.current = true;
                    // The income/accounts guard is already satisfied for this navigation
                    // (either it did not apply or the user just confirmed it). Preserve that
                    // acknowledgement so the next re-entry does not re-prompt and loop between
                    // income and funding.
                    skipIncomeAccountsPromptRef.current = true;
                    await journeySteps.onStepClick?.(stepNumber);
                };
                if (tryProceedToFundingNavigator(fundingProceed)) return;
            }
            if (checkStepDisabled(stepNumber) === false) {
                callMpGoalCalculator(
                    dispatch,
                    adviceGoals,
                    global?.orgInfo?.orgEmail || '',
                    goalCategoryPortfolioMappings
                );
            } else if (stepNumber === 3 && adviceGoals?.formFields?.length > 0 && !checkStepDisabled(stepNumber)) {
                const proceed = () => {
                    // make api call to api/advice/ews
                    if (checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_EWS_STREAMING_FE_AEX-3495'])) {
                        callUnifiedEWS(
                            dispatch,
                            global?.orgInfo?.incomeSourceType?.en,
                            global?.orgInfo?.investmentAccountType?.en,
                            adviceClient,
                            adviceGoals,
                            global?.orgInfo?.orgEmail || '',
                            tenant,
                            goalCategoryPortfolioMappings,
                            global?.goeConfig?.reallocSchedules
                        );
                    } else {
                        callSelectiveEWS(
                            dispatch,
                            global?.orgInfo?.incomeSourceType?.en,
                            global?.orgInfo?.investmentAccountType?.en,
                            adviceClient,
                            adviceGoals,
                            global?.orgInfo?.orgEmail || '',
                            tenant,
                            goalCategoryPortfolioMappings,
                            global?.goeConfig?.reallocSchedules
                        );
                    }
                };
                // The funding-missing prompt is already handled by the step-3 guard above.
                // On re-entry (skipFundingPrompt=true) we must not re-open the modal here,
                // otherwise it would appear a second time after the user confirms/cancels it.
                // Also restrict to forward navigation (coming from a step < 3) so navigating
                // back from step 4/5 to step 3 does not re-trigger the prompt.
                if (
                    !skipFundingPrompt &&
                    adviceClient?.step < 3 &&
                    tryProceedToFundingNavigator(async () => {
                        proceed();
                        await dispatch(updateCurrentStep(stepNumber));
                    })
                ) {
                    return;
                }
                proceed();
            } else if (stepNumber === 2 && adviceClient?.step === 1) {
                // Handling risk tolerance step separately
                // Only sync risk tolerance if the vertical scroller was touched
                if (hasRiskScrollerBeenTouched) {
                    handleSyncRiskTolerance();
                } else if (!adviceGoals?.global?.isPlanRun && riskPersistedInBackendRef.current !== true) {
                    // Mirror the Continue button: when the scroller wasn't touched we still persist
                    // the client on entering Goals if risk isn't in the backend yet, so navigating
                    // via the stepper saves just like the CTA.
                    await saveClientIfPayloadChanged(stepNumber);
                }
            } else if (stepNumber === 5 && !checkStepDisabled(stepNumber)) {
                dispatch(updateGlobalLoaderState(true));
                await triggerPlanExecution();
                dispatch(updateGlobalLoaderState(false));
            }
            await dispatch(updateCurrentStep(stepNumber));
        }
    };

    const generatePDFfilename = () =>
        `${clientFullName?.trim()}_${dayjs(new Date()).format('DD/MM/YY').replaceAll('/', '')}`;

    // Client name and risk options for RiskTolerance component
    const clientName = `${adviceClient?.clientFirstName || ''} ${adviceClient?.clientLastName || ''}`.trim();

    // Create risk options using organization data if available, otherwise use constants
    const riskOptions: ScrollerOption[] = useMemo(() => {
        return RISK_TOLERANCE_OPTIONS.map((option) => ({
            id: option.id,
            title: option.title,
            description: option.description
        }));
    }, [global?.orgInfo?.riskProfileAex?.en, t]);

    return (
        <>
            <CreatePdfReportsModal
                open={openPDFModal}
                handleClose={() => setOpenPDFModal(false)}
                handleSaveButtonAction={() => {
                    setOpenPDFModal(false);
                    setOpenPDFViewer(true);
                }}
                goalPortfolioMappings={goalPortfolioMappings}
                openPDFViewer={openPDFViewer}
                handleInvalidUrlToaster={handleInvalidUrlToaster}
                triggerBackendDownload={triggerBackendDownload}
            />
            <PDFViewer
                open={openPDFViewer}
                handleClose={() => setOpenPDFViewer(false)}
                filename={generatePDFfilename()}
                setPdfGenerateToast={setPdfGenerateToast}
            >
                <GeneratedPlanPDF />
            </PDFViewer>
            {factSheetsMissingToast.open && (
                <Toast
                    heading={t('FACTSHEETS_MISSING_TOAST_TEXT')}
                    type="alert"
                    messages={[]}
                    handleClose={() => setFactSheetsMissingToast({ open: false, generating: false })}
                    disabled={false}
                    isTimer={true}
                    disappearTime={5}
                />
            )}
            {savePlanToast.open && (
                <Toast
                    heading={t(savePlanToast.type === 'confirm' ? 'SAVE_PLAN_SUCCESS' : 'SAVE_PLAN_ERROR')}
                    type={savePlanToast.type}
                    messages={[]}
                    handleClose={() => setSavePlanToast({ open: false, type: 'confirm' })}
                    disabled={false}
                    isTimer={true}
                    disappearTime={5}
                />
            )}
            <PdfDownloadToast
                state={pdfGenerateToast}
                onClose={() => setPdfGenerateToast({ open: false, generating: false })}
            />
            <JourneyContainer>
                <StepperPills {...journeySteps} />
            </JourneyContainer>
            {activeDrawer && (
                <AddInvestmentAccounts
                    setOpenDrawer={(flag: boolean) => setActiveDrawer(flag)}
                    openDrawer={activeDrawer}
                    mode="edit"
                    callRunpipe={callRunpipe}
                    goalDataRecomendation={goalDataRecomendation}
                />
            )}
            {confirmModal && (
                <ConfirmationCentered
                    cancelButtonLabel={confirmModal.cancelLabel}
                    confirmButtonLabel={confirmModal.confirmLabel}
                    title={confirmModal.title}
                    content={confirmModal.content}
                    onCancellation={handleConfirmModalCancel}
                    onCloseIconClick={handleConfirmModalCancel}
                    onConfirmation={handleConfirmModalConfirm}
                    open
                    cancelButtonFirst={!!confirmModal.cancelButtonFirst}
                />
            )}
            <ProfileLeaveConfirmModal ref={leaveModalRef} />
        </>
    );
};
export default ClientJourneyFlow2;
